import type { UiWalletAccount } from "@wallet-standard/react"
import { createContext, useState, ReactNode, useContext, useMemo } from "react"

export type SelectedWalletAccountState = UiWalletAccount | undefined

// Create the context with a more descriptive name
export const SelectedWalletAccountContext = createContext<
  readonly [
    selectedWalletAccount: SelectedWalletAccountState,
    setSelectedWalletAccount: React.Dispatch<React.SetStateAction<SelectedWalletAccountState>>,
  ]
>([
  undefined /* selectedWalletAccount */,
  () => {
    throw new Error("SelectedWalletAccountContext not initialized")
  },
])

// Add a provider component for easier usage
export function SelectedWalletAccountProvider({
  children,
  initialAccount,
}: {
  children: ReactNode
  initialAccount?: SelectedWalletAccountState
}) {
  const [selectedWalletAccount, setSelectedWalletAccount] =
    useState<SelectedWalletAccountState>(initialAccount)

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => [selectedWalletAccount, setSelectedWalletAccount] as const,
    [selectedWalletAccount]
  )

  return (
    <SelectedWalletAccountContext.Provider value={contextValue}>
      {children}
    </SelectedWalletAccountContext.Provider>
  )
}

// Add a custom hook for easier consumption
export function useSelectedWalletAccount() {
  const context = useContext(SelectedWalletAccountContext)

  if (!context) {
    throw new Error("useSelectedWalletAccount must be used within a SelectedWalletAccountProvider")
  }

  return context
}
